import {
  AspectRatio,
  Resolution,
} from 'src/domains/calculators/utilities/types';
import { roundToNearestEven } from 'src/domains/calculators/utilities/round';

/*
 * Get protection resolution: the framing decision area at 0% protection.
 *
 * Evaluates a height-first and a width-first candidate (each locking one
 * axis exactly to the container and rounding the other to the nearest
 * even integer) and selects between them. When both candidates fit and
 * the target aspect ratio is wider than 16:9, Netflix UHD eligibility
 * (width >= 3840) is the primary selector; otherwise the candidate with
 * the smaller post-rounding aspect-ratio error wins, with height-first
 * preferred on exact ties.
 */
export function getProtectionResolution({
  aspectRatio,
  containerResolution,
  squeeze = 1,
  desqueezed = false,
}: {
  aspectRatio: AspectRatio;
  containerResolution: Resolution;
  squeeze: number;
  desqueezed?: boolean;
}): Resolution {
  const { width: cw, height: ch } = containerResolution;
  const effSqueeze = desqueezed ? 1 : squeeze;
  const [arNum, arDen] = aspectRatio;
  const effTarget = arNum / arDen / effSqueeze;

  // Height-first: lock height exact, round only the derived width.
  const heightFirst: Resolution = {
    width: roundToNearestEven((ch * arNum) / arDen / effSqueeze),
    height: ch,
  };

  // Width-first: lock width exact, round only the derived height.
  const widthFirst: Resolution = {
    width: cw,
    height: roundToNearestEven((cw * effSqueeze * arDen) / arNum),
  };

  const heightFirstFits = heightFirst.width > 0 && heightFirst.width <= cw;
  const widthFirstFits = widthFirst.height > 0 && widthFirst.height <= ch;

  if (heightFirstFits && !widthFirstFits) return heightFirst;
  if (widthFirstFits && !heightFirstFits) return widthFirst;
  if (!heightFirstFits && !widthFirstFits) return heightFirst;

  // Both candidates fit. When the target is wider than 16:9, Netflix UHD
  // eligibility (width >= 3840) is the primary selector.
  if (effTarget > 1.778) {
    const uhdHeightFirst = heightFirst.width >= 3840;
    const uhdWidthFirst = widthFirst.width >= 3840;
    if (uhdHeightFirst !== uhdWidthFirst) {
      return uhdWidthFirst ? widthFirst : heightFirst;
    }
  }

  // Prefer the candidate whose post-rounding aspect ratio is closer to the
  // target. Height-first wins exact ties.
  const errHeightFirst = Math.abs(
    heightFirst.width / heightFirst.height - effTarget
  );
  const errWidthFirst = Math.abs(
    widthFirst.width / widthFirst.height - effTarget
  );
  return errHeightFirst <= errWidthFirst ? heightFirst : widthFirst;
}
